import React, { useMemo } from 'react';
import { makeStyles } from '~/UIKit/Style';
import { Button } from '~/UIKit';
import { PRIMARY_BLUE, FITOGRAM_BLUE_LIGHT } from '~/Constants/Colors.constants';
import { Add, Remove } from '@mui/icons-material';
import cn from 'classnames';

const useStyles = makeStyles({
  ItemCreationCounterContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  ItemCreationCounterControls: {
    display: 'flex',
    paddingLeft: 10,
  },
  ItemCreationCounterControl: {
    height: 30,
    minWidth: 44.5,
    display: 'flex',
    color: PRIMARY_BLUE,
    justifyContent: 'center',
    border: `1px solid ${FITOGRAM_BLUE_LIGHT}66`,
    '& + &': {
      borderLeft: 'none',
    },
  },
});

interface ItemCreationCounterProps {
  value: number;
  onUpdate: (count: number) => void;
  formatValue?: (count: number) => React.ReactNode;
  min?: number | null;
  max?: number | null;
  className: string;
}
export const ItemCreationCounter = ({
  value,
  onUpdate,
  formatValue,
  min = null,
  max = null,
  className,
}: ItemCreationCounterProps) => {
  const { classes } = useStyles();
  const controlsProps = useMemo(
    () => [
      {
        key: 'decrease',
        content: <Remove />,
        onClick: () => onUpdate(min !== null && value <= min ? value : value - 1),
      },
      {
        key: 'increase',
        content: <Add />,
        onClick: () => onUpdate(max !== null && value >= max ? value : value + 1),
      },
    ],
    [min, max, value, onUpdate],
  );
  const formattedValue = useMemo(() => formatValue?.(value) ?? value, [formatValue, value]);

  return (
    <div className={cn(classes.ItemCreationCounterContainer, className)}>
      <div>{formattedValue}</div>
      <div className={classes.ItemCreationCounterControls}>
        {controlsProps.map(({ content, key, ...props }) => (
          <Button
            key={key}
            size="small"
            variant="text"
            className={classes.ItemCreationCounterControl}
            {...props}
          >
            {content}
          </Button>
        ))}
      </div>
    </div>
  );
};
